import { useState } from "react";
import { format, startOfQuarter, endOfQuarter } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  datePresets,
  getDateRange,
  normalizeDayRange,
  formatRangeSummary,
} from "@/pages/workshop/utils/dateRange";

export interface DateRangeValue {
  preset: string | null;
  /** Rango normalizado (from 00:00, to 23:59:59.999) listo para consultar. */
  range: { from: Date; to: Date } | null;
}

interface Props {
  preset: string | null;
  customFrom: Date | null;
  customTo: Date | null;
  onChange: (next: { preset: string | null; customFrom: Date | null; customTo: Date | null }) => void;
  className?: string;
  /** Años disponibles para el selector "Trimestre concreto". Por defecto: últimos 6 años. */
  availableYears?: number[];
}

/**
 * Selector unificado de rango de fechas. Toda la app debería usar este
 * componente para garantizar la MISMA normalización (00:00 / 23:59:59.999
 * en zona local) y la MISMA visualización del badge resumen.
 */
export function DateRangeFilter({ preset, customFrom, customTo, onChange, className, availableYears }: Props) {
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [isQuarterOpen, setIsQuarterOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = availableYears && availableYears.length > 0
    ? availableYears
    : Array.from({ length: 6 }, (_, i) => currentYear - i);

  const activeRange: { from: Date; to: Date } | null =
    preset === "custom" || preset === "quarter-pick"
      ? customFrom
        ? normalizeDayRange(customFrom, customTo ?? customFrom)
        : null
      : preset
      ? getDateRange(preset)
      : null;

  const handlePresetChange = (val: string) => {
    if (val === "custom") {
      onChange({ preset: "custom", customFrom, customTo });
      setIsCustomOpen(true);
    } else if (val === "quarter-pick") {
      // Aplicar el trimestre actual del año actual como punto de partida
      applyQuarter(Math.floor(new Date().getMonth() / 3) + 1, currentYear);
      setIsQuarterOpen(true);
    } else {
      onChange({ preset: val || null, customFrom: null, customTo: null });
    }
  };

  const applyQuarter = (quarter: number, year: number) => {
    const base = new Date(year, (quarter - 1) * 3, 1);
    const normalized = normalizeDayRange(startOfQuarter(base), endOfQuarter(base));
    onChange({ preset: "quarter-pick", customFrom: normalized.from, customTo: normalized.to });
  };

  const selectedQuarter = preset === "quarter-pick" && customFrom
    ? Math.floor(customFrom.getMonth() / 3) + 1
    : Math.floor(new Date().getMonth() / 3) + 1;
  const selectedYear = preset === "quarter-pick" && customFrom
    ? customFrom.getFullYear()
    : currentYear;

  const presetLabel = preset === "quarter-pick" && customFrom
    ? `T${selectedQuarter} ${selectedYear}`
    : datePresets.find((p) => p.value === preset)?.label ?? "Rango";

  return (
    <div className={cn("flex items-center gap-2 flex-wrap", className)}>
      <Select value={preset || ""} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-48 bg-secondary border-border h-9">
          <SelectValue placeholder="Filtrar por fecha" />
        </SelectTrigger>
        <SelectContent className="bg-popover border-border">
          {datePresets.map((p) => (
            <SelectItem key={p.value} value={p.value}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {preset === "quarter-pick" && (
        <Popover open={isQuarterOpen} onOpenChange={setIsQuarterOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="bg-secondary border-border h-9">
              <CalendarIcon className="mr-2 h-4 w-4" />
              T{selectedQuarter} {selectedYear}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3 bg-popover border-border z-50" align="start">
            <div className="flex items-center gap-2">
              <Select
                value={String(selectedQuarter)}
                onValueChange={(v) => applyQuarter(Number(v), selectedYear)}
              >
                <SelectTrigger className="w-24 bg-secondary border-border h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {[1, 2, 3, 4].map((q) => (
                    <SelectItem key={q} value={String(q)}>T{q}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={String(selectedYear)}
                onValueChange={(v) => applyQuarter(selectedQuarter, Number(v))}
              >
                <SelectTrigger className="w-28 bg-secondary border-border h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {preset === "custom" && (
        <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="bg-secondary border-border h-9">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {customFrom ? (
                <>
                  {format(customFrom, "dd/MM/yyyy")}
                  {customTo && customTo.getTime() !== customFrom.getTime()
                    ? ` - ${format(customTo, "dd/MM/yyyy")}`
                    : ""}
                </>
              ) : (
                "Seleccionar fechas"
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-popover border-border z-50" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={customFrom ?? undefined}
              selected={
                customFrom ? { from: customFrom, to: customTo ?? undefined } : undefined
              }
              onSelect={(range) => {
                if (range?.from) {
                  const normalized = normalizeDayRange(range.from, range.to ?? range.from);
                  onChange({
                    preset: "custom",
                    customFrom: normalized.from,
                    customTo: normalized.to,
                  });
                  if (range.to) setIsCustomOpen(false);
                } else {
                  onChange({ preset: "custom", customFrom: null, customTo: null });
                }
              }}
              numberOfMonths={2}
              locale={es}
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
      )}

      {preset && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange({ preset: null, customFrom: null, customTo: null })}
          className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" /> Quitar filtro
        </Button>
      )}

      {activeRange && (
        <Badge
          variant="secondary"
          className="h-9 px-3 gap-1.5 text-xs font-medium"
          title="Rango aplicado al filtro"
        >
          <CalendarIcon className="h-3.5 w-3.5" />
          {presetLabel}:{" "}
          {formatRangeSummary(activeRange.from, activeRange.to)}
        </Badge>
      )}
    </div>
  );
}
